/**
 * ProseMirror to FlowBlock Adapter
 *
 * Converts ProseMirror documents into FlowBlock[] for the layout engine pipeline.
 *
 * Responsibilities:
 * - Parse paragraph nodes from PM document
 * - Split text content into styled runs based on mark boundaries
 * - Generate deterministic BlockIds for layout tracking
 * - Normalize whitespace and handle empty paragraphs
 */

import type { FlowBlock, ParagraphAttrs, ParagraphBlock } from '@superdoc/contracts';
import { resolveSectionDirection } from './direction/resolveSectionDirection.js';
import { isValidTrackedMode } from './tracked-changes.js';
import {
  analyzeSectionRanges,
  createSectionBreakBlock,
  maybeEmitNextSectionBreakForNode,
  publishSectionMetadata,
} from './sections/index.js';
import { normalizePrefix, buildPositionMap, createBlockIdGenerator } from './utilities.js';
import {
  paragraphToFlowBlocks,
  contentBlockNodeToDrawingBlock,
  imageNodeToBlock,
  handleImageNode,
  vectorShapeNodeToDrawingBlock,
  shapeGroupNodeToDrawingBlock,
  shapeContainerNodeToDrawingBlock,
  shapeTextboxNodeToDrawingBlock,
  handleVectorShapeNode,
  handleShapeGroupNode,
  handleShapeContainerNode,
  handleShapeTextboxNode,
  tableNodeToBlock,
  handleTableNode,
  hydrateImageBlocks,
  handleParagraphNode,
} from './converters/index.js';
import { chartNodeToDrawingBlock, handleChartNode } from './converters/chart.js';
import { handleMathBlockNode } from './converters/math-block.js';
import {
  handleTableOfContentsNode,
  handleIndexNode,
  handleStructuredContentBlockNode,
  handleDocumentSectionNode,
  handleDocumentPartObjectNode,
  handleBibliographyNode,
  handleTableOfAuthoritiesNode,
} from './sdt/index.js';
import type {
  PMNode,
  TrackedChangesConfig,
  HyperlinkConfig,
  FlowBlocksResult,
  AdapterOptions,
  BatchAdapterOptions,
  NodeHandlerContext,
  NodeHandler,
  NestedConverters,
  ConverterContext,
  PMDocumentMap,
} from './types.js';

const DEFAULT_FONT = 'Times New Roman';
const DEFAULT_SIZE = 10 / 0.75; // 10pt in pixels

function resolveSectionDirectionFromSectPr(sectPr: unknown): 'ltr' | 'rtl' | undefined {
  if (!sectPr || typeof sectPr !== 'object') return undefined;
  const elements = (sectPr as { elements?: Array<{ name?: string; attributes?: Record<string, unknown> }> }).elements;
  if (!Array.isArray(elements)) return undefined;
  const bidi = elements.find((element) => element?.name === 'w:bidi');
  if (!bidi) return undefined;
  const val = bidi.attributes?.['w:val'] ?? bidi.attributes?.val;
  if (val === '0' || val === 0 || val === false || val === 'false' || val === 'off') return 'ltr';
  return 'rtl';
}

/**
 * Dispatch map for node type handlers.
 * Maps node type names to their corresponding handler functions.
 */
export const nodeHandlers: Record<string, NodeHandler> = {
  paragraph: handleParagraphNode,
  tableOfContents: handleTableOfContentsNode,
  index: handleIndexNode,
  structuredContentBlock: handleStructuredContentBlockNode,
  documentSection: handleDocumentSectionNode,
  table: handleTableNode,
  documentPartObject: handleDocumentPartObjectNode,
  bibliography: handleBibliographyNode,
  tableOfAuthorities: handleTableOfAuthoritiesNode,
  image: handleImageNode,
  vectorShape: handleVectorShapeNode,
  shapeGroup: handleShapeGroupNode,
  shapeContainer: handleShapeContainerNode,
  shapeTextbox: handleShapeTextboxNode,
  chart: handleChartNode,
  mathBlock: handleMathBlockNode,
};

const converters: NestedConverters = {
  contentBlockNodeToDrawingBlock,
  imageNodeToBlock,
  vectorShapeNodeToDrawingBlock,
  shapeGroupNodeToDrawingBlock,
  shapeContainerNodeToDrawingBlock,
  shapeTextboxNodeToDrawingBlock,
  chartNodeToDrawingBlock,
  tableNodeToBlock,
  paragraphToFlowBlocks,
};

/**
 * Convert a ProseMirror document to FlowBlock array with bookmark tracking.
 *
 * Returns both blocks and a bookmark map for two-pass layout with
 * cross-reference resolution (e.g., TOC page numbers, PAGEREF fields).
 *
 * Use this when you need to resolve page references dynamically:
 * 1. Call toFlowBlocks() to get blocks + bookmarks
 * 2. Run first layout pass to position fragments
 * 3. Build anchor map from bookmarks and fragment PM positions
 * 4. Resolve pageRef tokens to actual page numbers
 * 5. Re-measure affected paragraphs (TOC entries)
 * 6. Run second layout pass for final positioning
 *
 * @param pmDoc - ProseMirror document
 * @param options - Optional configuration
 * @returns Object with blocks and bookmark position map
 *
 * @example
 * ```typescript
 * const { blocks, bookmarks } = toFlowBlocks(pmDoc);
 * const layout = layoutDocument(blocks, measures, options);
 * const anchorMap = buildAnchorMap(bookmarks, layout);
 * resolvePageRefTokens(blocks, anchorMap);
 * const finalLayout = layoutDocument(blocks, newMeasures, options);
 * ```
 */
export function toFlowBlocks(pmDoc: PMNode | object, options?: AdapterOptions): FlowBlocksResult {
  const defaultFont = options?.defaultFont ?? DEFAULT_FONT;
  const defaultSize = options?.defaultSize ?? DEFAULT_SIZE;
  const instrumentation = options?.instrumentation;
  const idPrefix = normalizePrefix(options?.blockIdPrefix);

  const doc = pmDoc as PMNode;
  const flowBlockCache = options?.flowBlockCache;

  // Begin cache cycle if cache is provided
  flowBlockCache?.begin();

  if (!doc.content) {
    flowBlockCache?.commit();
    return { blocks: [], bookmarks: new Map() };
  }

  const trackedChangesMode = isValidTrackedMode(options?.trackedChangesMode) ? options.trackedChangesMode : 'review';
  const enableTrackedChanges = options?.enableTrackedChanges ?? true;
  const trackedChangesConfig: TrackedChangesConfig = {
    mode: trackedChangesMode,
    enabled: enableTrackedChanges,
  };
  const hyperlinkConfig: HyperlinkConfig = {
    enableRichHyperlinks: options?.enableRichHyperlinks ?? false,
  };
  const enableComments = options?.enableComments ?? true;
  const themeColors = options?.themeColors;
  const converterContext: ConverterContext = normalizeConverterContext(
    options?.converterContext,
    defaultFont,
    defaultSize,
  );
  if (options?.showBookmarks !== undefined) {
    converterContext.showBookmarks = options.showBookmarks;
  }
  if (converterContext.showBookmarks) {
    converterContext.renderedBookmarkIds = new Set<string>();
  }

  const blocks: FlowBlock[] = [];
  const bookmarks = new Map<string, number>();
  const positions =
    options?.positions ??
    (options?.atomNodeTypes ? buildPositionMap(doc, { atomNodeTypes: options.atomNodeTypes }) : buildPositionMap(doc));

  const nextBlockId = createBlockIdGenerator(idPrefix);
  const blockCounts: Partial<Record<FlowBlock['kind'], number>> = {};
  const recordBlockKind = (kind: FlowBlock['kind']) => {
    blockCounts[kind] = (blockCounts[kind] ?? 0) + 1;
  };

  // Range-aware section analysis (matches toFlowBlocks semantics)
  const bodySectionProps = doc.attrs?.bodySectPr ?? doc.attrs?.sectPr;
  const sectionRanges = options?.emitSectionBreaks ? analyzeSectionRanges(doc, bodySectionProps) : [];
  const firstSectPr = sectionRanges[0]?.sectPr ?? bodySectionProps;
  converterContext.sectionDirection =
    converterContext.sectionDirection ?? resolveSectionDirectionFromSectPr(firstSectPr);
  // Body-level section direction context: writing-mode inherits to paragraphs that
  // omit their own w:textDirection, per ECMA §17.3.1.41. Multi-section docs (each
  // section with its own sectPr) and table-cell direction context are not yet
  // plumbed through - SD-2777 closes that gap.
  // Always recompute per call: when callers reuse the same ConverterContext
  // across documents (toFlowBlocksMap is the obvious case), the prior `??`
  // cache let the first document's writing-mode survive into later documents.
  converterContext.sectionDirectionContext = resolveSectionDirection(firstSectPr);
  publishSectionMetadata(sectionRanges, options);

  // Emit first section break before content to set initial properties.
  // The isFirstSection flag tells the layout engine to apply properties immediately
  // without forcing a page break (since there's no content yet), but we preserve
  // the section's actual type for semantic correctness.
  if (sectionRanges.length > 0 && sectionRanges[0]) {
    const sectionBreak = createSectionBreakBlock(sectionRanges[0], nextBlockId, { isFirstSection: true });
    blocks.push(sectionBreak);
    recordBlockKind(sectionBreak.kind);
  }

  // Build handler context for node processing
  const handlerContext: NodeHandlerContext = {
    blocks,
    recordBlockKind,
    nextBlockId,
    blockIdPrefix: idPrefix,
    storyKey: options?.storyKey,
    positions,
    defaultFont,
    defaultSize,
    converterContext,
    trackedChangesConfig,
    hyperlinkConfig,
    enableComments,
    bookmarks,
    sectionState: {
      ranges: sectionRanges,
      currentSectionIndex: 0,
      currentParagraphIndex: 0,
      currentNodeIndex: 0,
    },
    converters,
    themeColors,
    flowBlockCache,
    trackedListMarkerOffsets: new Map<string, number>(),
    trackedListLastOrdinals: new Map<string, number>(),
  };

  // Process nodes using handler dispatch pattern. Before each top-level node
  // we flush any pending section break whose `startNodeIndex` matches the
  // current position — this keeps non-paragraph nodes (tables, top-level
  // drawings) in their correct end-tagged section per ECMA-376 §17.6.17.
  doc.content.forEach((node) => {
    maybeEmitNextSectionBreakForNode({
      sectionState: handlerContext.sectionState!,
      nextBlockId,
      pushBlock: (block) => {
        blocks.push(block);
        recordBlockKind(block.kind);
      },
    });
    const handler = nodeHandlers[node.type];
    if (handler) {
      handler(node, handlerContext);
    }
    handlerContext.sectionState!.currentNodeIndex++;
  });

  // Ensure final body section is emitted only if not already emitted during paragraph processing.
  // The final section break is emitted by handleParagraphNode when entering the last section,
  // so we only need to emit it here if currentSectionIndex hasn't reached the last section yet.
  if (sectionRanges.length > 0) {
    const lastSectionIndex = sectionRanges.length - 1;
    const lastSection = sectionRanges[lastSectionIndex];
    // Only emit if we haven't processed the last section yet
    if (handlerContext.sectionState!.currentSectionIndex < lastSectionIndex) {
      const sectionBreak = createSectionBreakBlock(lastSection, nextBlockId);
      blocks.push(sectionBreak);
      recordBlockKind(sectionBreak.kind);
    }
  }

  instrumentation?.log?.({ totalBlocks: blocks.length, blockCounts, bookmarks: bookmarks.size });
  const hydratedBlocks = hydrateImageBlocks(blocks, options?.mediaFiles);

  // Post-process: Merge drop-cap paragraphs with their following text paragraphs
  const dropCapMergedBlocks = mergeDropCapParagraphs(hydratedBlocks);

  // Post-process: Fuse paragraphs whose paragraph-mark rPr has w:vanish into
  // the next paragraph, matching Word's de-facto behaviour (SD-3269).
  const mergedBlocks = mergeFusedParagraphs(dropCapMergedBlocks);

  // Commit cache cycle - swaps next to previous, retaining only blocks seen this render
  flowBlockCache?.commit();

  return { blocks: mergedBlocks, bookmarks };
}

/**
 * Batch convert a map of ProseMirror documents to FlowBlocks.
 *
 * Applies optional per-document block ID prefixes via blockIdPrefixFactory.
 *
 * @param documents - Map of document keys to PM nodes
 * @param options - Optional batch options (shared across documents)
 * @returns Map of document keys to FlowBlock arrays
 */
export function toFlowBlocksMap(documents: PMDocumentMap, options?: BatchAdapterOptions): Record<string, FlowBlock[]> {
  const results: Record<string, FlowBlock[]> = {};
  const prefixFactory = options?.blockIdPrefixFactory;

  Object.entries(documents).forEach(([key, doc]) => {
    if (doc == null) return;
    const blockIdPrefix = prefixFactory ? prefixFactory(key) : options?.blockIdPrefix;
    const result = toFlowBlocks(doc, { ...options, blockIdPrefix });
    results[key] = result.blocks;
  });

  return results;
}

/**
 * Merge drop-cap paragraphs with their following text paragraphs.
 *
 * In DOCX, drop caps are encoded as separate paragraphs containing just the
 * drop cap letter(s) with w:framePr/@w:dropCap. This function:
 * 1. Identifies paragraphs with dropCapDescriptor (the drop-cap letter paragraph)
 * 2. Merges them with the following paragraph (the text paragraph)
 * 3. Transfers the dropCapDescriptor to the merged paragraph
 * 4. Removes the original drop-cap-only paragraph
 *
 * @param blocks - Array of flow blocks to process
 * @returns New array with drop-cap paragraphs merged
 */
function mergeDropCapParagraphs(blocks: FlowBlock[]): FlowBlock[] {
  const result: FlowBlock[] = [];
  let i = 0;

  while (i < blocks.length) {
    const block = blocks[i];

    // Check if this is a drop-cap paragraph (has dropCapDescriptor)
    if (block.kind === 'paragraph' && block.attrs?.dropCapDescriptor && i + 1 < blocks.length) {
      const dropCapBlock = block as ParagraphBlock;
      const nextBlock = blocks[i + 1];

      // Check if next block is a paragraph we can merge with
      if (nextBlock.kind === 'paragraph') {
        const textBlock = nextBlock as ParagraphBlock;

        // Create merged paragraph:
        // - Use the text block's ID and most attributes
        // - Prepend the drop-cap letter to the runs (not the runs themselves,
        //   as the letter is already in the dropCapDescriptor.run)
        // - Transfer the dropCapDescriptor from the drop-cap block
        const mergedBlock: ParagraphBlock = {
          kind: 'paragraph',
          id: textBlock.id,
          runs: textBlock.runs,
          attrs: {
            ...textBlock.attrs,
            dropCapDescriptor: dropCapBlock.attrs?.dropCapDescriptor,
            // Clear the legacy dropCap flag on the merged block
            dropCap: undefined,
          },
        };

        result.push(mergedBlock);
        // Skip both the drop-cap block and the text block
        i += 2;
        continue;
      }
    }

    // Not a drop-cap or no following paragraph - keep as-is
    result.push(block);
    i += 1;
  }

  return result;
}

/**
 * Fuse paragraphs whose paragraph-mark rPr carries `w:vanish` into the next
 * paragraph block. Word 16.0 treats the hidden paragraph-mark glyph as a
 * suppressed visible break, so the next paragraph flows on the same line and
 * its auto-generated numbering marker disappears with the absorbed block.
 * Numbering counters still advance per OOXML paragraph, so subsequent items
 * skip a slot (matches `pdftotext` extraction of Word's own PDF export).
 *
 * ECMA-376 §17.3.2.36 reads as if `w:specVanish` is the trigger, but the
 * Word-native fixture matrix (vanish only / specVanish only) shows Word
 * actually triggers on `w:vanish`. The matrix is documented in SD-3269.
 *
 * Strategy: keep the predecessor's block id, attrs, marker, and indent;
 * concatenate `predecessor.runs ++ successor.runs`; drop the successor block.
 * Chains collapse left-to-right.
 *
 * @param blocks - Array of flow blocks to process
 * @returns New array with paragraph-mark-vanished paragraphs fused into their successors
 */
function mergeFusedParagraphs(blocks: FlowBlock[]): FlowBlock[] {
  const result: FlowBlock[] = [];

  for (const block of blocks) {
    const prev = result.length > 0 ? result[result.length - 1] : undefined;
    if (
      block.kind === 'paragraph' &&
      prev?.kind === 'paragraph' &&
      (prev as ParagraphBlock).attrs?.suppressParagraphBreak
    ) {
      const head = prev as ParagraphBlock;
      const tail = block as ParagraphBlock;
      // Keep the predecessor's id/attrs (style, marker, indent) and append
      // the successor's runs. The successor's marker is implicitly dropped
      // because its block no longer exists. Carry the flag forward only when
      // the successor itself sets it, so chains keep collapsing left-to-right.
      const mergedAttrs: ParagraphAttrs = { ...head.attrs };
      if (tail.attrs?.suppressParagraphBreak) {
        mergedAttrs.suppressParagraphBreak = true;
      } else {
        delete (mergedAttrs as Record<string, unknown>).suppressParagraphBreak;
      }
      const merged: ParagraphBlock = {
        kind: 'paragraph',
        id: head.id,
        runs: [...head.runs, ...tail.runs],
        attrs: mergedAttrs,
        ...(head.sourceAnchor ? { sourceAnchor: head.sourceAnchor } : {}),
      };
      result[result.length - 1] = merged;
      continue;
    }
    result.push(block);
  }

  return result;
}

/**
 * Normalize and populate the converter context with defaults.
 *
 * Ensures that essential properties like default font and size
 * are set in the converter context for consistent styling.
 *
 * @param context - Existing converter context (may be undefined)
 * @param defaultFont - Default font family to use
 * @param defaultSize - Default font size in pixels
 * @returns Normalized converter context
 */
function normalizeConverterContext(
  context: ConverterContext | undefined,
  defaultFont: string,
  defaultSize: number,
): ConverterContext {
  if (!context) {
    context = {
      translatedNumbering: {},
      translatedLinkedStyles: {
        docDefaults: {},
        latentStyles: {},
        styles: {},
      },
    };
  }

  if (!context.translatedLinkedStyles.docDefaults) {
    context.translatedLinkedStyles.docDefaults = {};
  }
  if (!context.translatedLinkedStyles.docDefaults.runProperties) {
    context.translatedLinkedStyles.docDefaults.runProperties = {};
  }
  if (!context.translatedLinkedStyles.docDefaults.runProperties.fontFamily) {
    context.translatedLinkedStyles.docDefaults.runProperties.fontFamily = {};
  }
  if (!context.translatedLinkedStyles.docDefaults.runProperties.fontFamily.ascii) {
    context.translatedLinkedStyles.docDefaults.runProperties.fontFamily.ascii = defaultFont;
  }
  if (!context.translatedLinkedStyles.docDefaults.runProperties.fontSize) {
    context.translatedLinkedStyles.docDefaults.runProperties.fontSize = defaultSize * 0.75 * 2; // size in half-points
  }

  return context;
}
